/**
 * Vivre Control Plane — Reusable Components Index
 *
 * Import from this file for convenience:
 *   import { StatusChip, PlanBadge, HealthBar, CopyButton, PageHeader } from "Pages/controlPlane/components";
 */

// Re-export named components
export { default as StatusChip } from "./StatusChip";
export { default as PlanBadge } from "./PlanBadge";
export { default as HealthBar } from "./HealthBar";
export { default as KpiCard } from "./KpiCard";
export { default as GlobalSearch } from "./GlobalSearch";
export { default as ConfirmationModal } from "./ConfirmationModal";
export { default as BulkActionBar } from "./BulkActionBar";

// ─── Inline Utility Components ───────────────────────────────────────────────

/**
 * CopyButton — Small clipboard copy icon button
 * Usage: <CopyButton text="some-id" />
 */
export function CopyButton({ text, size = 14 }) {
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
  };

  return (
    <span
      onClick={handleCopy}
      title="Copy to clipboard"
      className="material-icons-outlined"
      style={{
        cursor: "pointer",
        color: "#BDC3C7",
        fontSize: size,
        marginLeft: 4,
        transition: "color 0.15s",
        verticalAlign: "middle",
      }}
      onMouseEnter={(e) => (e.target.style.color = "#00ACC4")}
      onMouseLeave={(e) => (e.target.style.color = "#BDC3C7")}
    >
      content_copy
    </span>
  );
}

/**
 * PageHeader — Consistent page header with breadcrumb
 * Usage: <PageHeader section="Tenants" title="Tenant Management" />
 */
export function PageHeader({ section, title, actions }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span style={{ color: "#5D6D7E", fontSize: 12 }}>Control Plane</span>
        <span style={{ color: "#BDC3C7" }}>›</span>
        <span style={{ color: "#1E3A5F", fontSize: 12, fontWeight: 600 }}>{section}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1E3A5F" }}>{title}</h1>
        {actions && <div style={{ display: "flex", gap: 12 }}>{actions}</div>}
      </div>
    </div>
  );
}

/**
 * TenantId — Monospace tenant ID with copy button
 * Usage: <TenantId id="t-001" />
 */
export function TenantId({ id }) {
  if (!id) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
      <span style={{ fontFamily: "monospace", fontSize: 11, color: "#00ACC4" }}>{id}</span>
      <CopyButton text={id} size={12} />
    </span>
  );
}

/**
 * SectionTitle — Uppercase section label used inside panels/cards
 * Usage: <SectionTitle>Feature Flags</SectionTitle>
 */
export function SectionTitle({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: "#1E3A5F",
        textTransform: "uppercase",
        letterSpacing: 0.4,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

/**
 * EmptyState — Shown when a table/list has no data
 * Usage: <EmptyState message="No tenants found" icon="search" />
 */
export function EmptyState({ message = "No data found", icon = "inbox" }) {
  return (
    <div style={{ padding: 40, textAlign: "center" }}>
      <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>
        {icon === "search" ? "🔍" : icon === "inbox" ? "📭" : "📋"}
      </div>
      <div style={{ color: "#5D6D7E", fontSize: 14 }}>{message}</div>
    </div>
  );
}

/**
 * ActionButton — Gradient primary action button (e.g. "+ New Tenant")
 * Usage: <ActionButton onClick={fn} label="+ New Tenant" />
 */
export function ActionButton({ onClick, label, icon }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "linear-gradient(135deg, #00ACC4 0%, #2F36CB 100%)",
        color: "#fff",
        border: "none",
        borderRadius: 8,
        padding: "8px 18px",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {icon && <span>{icon}</span>}
      {label}
    </button>
  );
}

/**
 * FilterPill — Status filter button used in filter bars
 * Usage: <FilterPill label="Active" active={filter === "Active"} onClick={() => setFilter("Active")} />
 */
export function FilterPill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "4px 12px",
        borderRadius: 20,
        border: `1px solid ${active ? "#00ACC4" : "#E8EDF2"}`,
        background: active ? "#E0F7FA" : "#fff",
        color: active ? "#00ACC4" : "#5D6D7E",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

/**
 * TableWrapper — Consistent table container with border and rounded corners
 * Usage: <TableWrapper>{children}</TableWrapper>
 */
export function TableWrapper({ children }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E8EDF2",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
}

/**
 * TableHeader — Consistent table header row
 * Usage: <TableHeader columns={["Name", "Status", "Plan"]} />
 */
export function TableHeader({ columns }) {
  return (
    <thead>
      <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E8EDF2" }}>
        {columns.map((h) => (
          <th
            key={h}
            style={{
              padding: "10px 14px",
              textAlign: "left",
              color: "#5D6D7E",
              fontWeight: 600,
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              whiteSpace: "nowrap",
            }}
          >
            {h}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/**
 * TableFooter — Consistent table footer with count
 * Usage: <TableFooter showing={5} total={20} label="tenants" />
 */
export function TableFooter({ showing, total, label = "items" }) {
  return (
    <div
      style={{
        padding: "10px 16px",
        borderTop: "1px solid #F0F3F6",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <span style={{ fontSize: 12, color: "#5D6D7E" }}>
        Showing {showing} of {total} {label}
      </span>
    </div>
  );
}
